import { useState } from 'react';
import type { TemplateVariable } from '../hooks/useDocumentTemplates';

interface VariableInserterProps {
  variables: TemplateVariable[];
  onInsert: (variableKey: string) => void;
}

/**
 * Sidebar panel that displays available template variables grouped by source.
 * Click a variable to insert it at the cursor position in the active editor.
 */
export function VariableInserter({ variables, onInsert }: VariableInserterProps) {
  const [searchTerm, setSearchTerm] = useState('');

  // Group variables by source
  const grouped = variables.reduce(
    (acc, variable) => {
      const source = variable.source || 'other';
      if (!acc[source]) acc[source] = [];
      acc[source].push(variable);
      return acc;
    },
    {} as Record<string, TemplateVariable[]>,
  );

  // Filter by search
  const filteredGroups = Object.entries(grouped).reduce(
    (acc, [source, vars]) => {
      const filtered = vars.filter(
        (v) =>
          v.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
          v.label.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      if (filtered.length > 0) acc[source] = filtered;
      return acc;
    },
    {} as Record<string, TemplateVariable[]>,
  );

  return (
    <div className="w-64 shrink-0 rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
      <div className="border-b border-gray-200 p-3 dark:border-gray-700">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">Variables</h3>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Click to insert</p>
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="mt-2 w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-indigo-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          aria-label="Search variables"
        />
      </div>

      <div className="max-h-[500px] overflow-y-auto p-2">
        {Object.keys(filteredGroups).length === 0 ? (
          <p className="p-2 text-xs text-gray-500 dark:text-gray-400">No variables found</p>
        ) : (
          Object.entries(filteredGroups).map(([source, vars]) => (
            <div key={source} className="mb-3">
              <h4 className="mb-1 px-1 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {source}
              </h4>
              <div className="space-y-0.5">
                {vars.map((variable) => (
                  <button
                    key={variable.key}
                    onClick={() => onInsert(variable.key)}
                    className="block w-full rounded px-2 py-1 text-left text-xs hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
                    title={variable.description || variable.key}
                  >
                    <span className="font-mono text-indigo-600 dark:text-indigo-400">
                      {`{{${variable.key}}}`}
                    </span>
                    <span className="ml-1 text-gray-500 dark:text-gray-400">{variable.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
